import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Loader2, Plus, Trash2, AlertCircle, CheckCircle2, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { EMISSION_CATEGORIES } from '../lib/constants';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Column definitions for the spreadsheet (matches the custom mapping template)
const COLUMNS = [
  { key: 'desired_emission_factor_id', label: 'Emission Factor ID', type: 'number', required: true, width: 150 },
  { key: 'category', label: 'Category', type: 'select', required: true, width: 220, options: 'categories' },
  { key: 'sub_category', label: 'Sub Category', type: 'text', required: true, width: 180 },
  { key: 'type', label: 'Type', type: 'text', required: false, width: 150 },
  { key: 'sub_type', label: 'Sub Type', type: 'text', required: false, width: 150 },
  { key: 'additional_information', label: 'Additional Information', type: 'text', required: false, width: 320 },
];

const createEmptyRow = () => {
  const row = { _id: Date.now() + Math.random(), _errors: {}, _status: 'empty' };
  COLUMNS.forEach(col => {
    row[col.key] = '';
  });
  return row;
};

const validateRow = (row) => {
  const errors = {};

  COLUMNS.forEach(col => {
    if (col.required && !row[col.key] && row[col.key] !== 0) {
      errors[col.key] = `${col.label} is required`;
    }
  });

  if (row.desired_emission_factor_id !== '' && row.desired_emission_factor_id !== null && row.desired_emission_factor_id !== undefined) {
    const num = Number(row.desired_emission_factor_id);
    if (isNaN(num) || !Number.isInteger(num) || num <= 0) {
      errors.desired_emission_factor_id = 'Must be a positive whole number';
    }
  }

  return errors;
};

const rowHasData = (row) => {
  return COLUMNS.some(col => {
    const val = row[col.key];
    return val !== '' && val !== null && val !== undefined;
  });
};

export default function BulkCustomMappingPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(() => Array(5).fill(null).map(createEmptyRow));
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [submitResults, setSubmitResults] = useState(null);
  const tableRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  const updateCell = useCallback((rowIndex, key, value) => {
    setRows(prev => {
      const newRows = [...prev];
      const actualValue = value === '__empty__' ? '' : value;
      newRows[rowIndex] = { ...newRows[rowIndex], [key]: actualValue };
      return newRows;
    });
  }, []);

  const addRows = useCallback((count = 5) => {
    setRows(prev => [...prev, ...Array(count).fill(null).map(createEmptyRow)]);
  }, []);

  const deleteRow = useCallback((rowIndex) => {
    setRows(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== rowIndex);
    });
  }, []);

  const clearAll = useCallback(() => {
    setRows(Array(5).fill(null).map(createEmptyRow));
    setSubmitResults(null);
    setUploadResults(null);
  }, []);

  const downloadTemplate = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/custom-mappings/bulk-template`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'custom_mappings_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
      console.error(error);
    }
  }, []);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (fileInputRef.current) fileInputRef.current.value = '';

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      toast.error('Please upload a .xlsx file');
      return;
    }

    setUploadingFile(true);
    setUploadResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/custom-mappings/bulk-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const results = response.data;
      setUploadResults(results);

      if (results.failed.length === 0) {
        toast.success(`Successfully uploaded ${results.success.length} custom mapping(s)`);
      } else if (results.success.length > 0) {
        toast.warning(`${results.success.length} uploaded, ${results.failed.length} failed`);
      } else {
        toast.error(`All ${results.failed.length} row(s) failed. Check the errors below.`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Upload failed: ${msg}`);
      setUploadResults({ success: [], failed: [{ row: '-', errors: [msg] }], total: 0 });
    } finally {
      setUploadingFile(false);
    }
  }, []);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('text');
    if (!pastedText) return;

    const pastedRows = pastedText.split('\n').filter(row => row.trim());
    const parsedData = pastedRows.map(row => row.split('\t'));
    if (parsedData.length === 0) return;

    const activeElement = document.activeElement;
    const cellMatch = activeElement?.dataset?.cell?.match(/(\d+)-(\d+)/);
    let startRow = 0;
    let startCol = 0;
    if (cellMatch) {
      startRow = parseInt(cellMatch[1]);
      startCol = parseInt(cellMatch[2]);
    }

    setRows(prev => {
      const newRows = [...prev];
      while (newRows.length < startRow + parsedData.length) {
        newRows.push(createEmptyRow());
      }
      parsedData.forEach((rowData, rowOffset) => {
        rowData.forEach((cellValue, colOffset) => {
          const targetRow = startRow + rowOffset;
          const targetCol = startCol + colOffset;
          if (targetCol < COLUMNS.length && targetRow < newRows.length) {
            const col = COLUMNS[targetCol];
            let value = cellValue.trim();
            if (col.type === 'number') {
              value = value === '' ? '' : Number(value);
            }
            newRows[targetRow] = { ...newRows[targetRow], [col.key]: value };
          }
        });
      });
      return newRows;
    });

    toast.success(`Pasted ${parsedData.length} rows`);
  }, []);

  const handleSubmit = useCallback(() => {
    const rowsWithData = rows.filter(rowHasData);
    if (rowsWithData.length === 0) {
      toast.error('No data to submit');
      return;
    }

    let hasErrors = false;
    const validatedRows = rows.map(row => {
      if (!rowHasData(row)) {
        return { ...row, _errors: {}, _status: 'empty' };
      }
      const errors = validateRow(row);
      if (Object.keys(errors).length > 0) {
        hasErrors = true;
        return { ...row, _errors: errors, _status: 'error' };
      }
      return { ...row, _errors: {}, _status: 'valid' };
    });
    setRows(validatedRows);

    if (hasErrors) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setConfirmDialog(true);
  }, [rows]);

  const confirmSubmit = useCallback(async () => {
    setConfirmDialog(false);
    setSubmitting(true);

    const rowsToSubmit = rows.filter(row => rowHasData(row) && Object.keys(row._errors || {}).length === 0);
    const results = { success: [], failed: [] };

    for (let i = 0; i < rowsToSubmit.length; i++) {
      const row = rowsToSubmit[i];
      try {
        const data = {
          category: row.category,
          sub_category: row.sub_category,
          type: row.type || null,
          sub_type: row.sub_type || null,
          desired_emission_factor_id: Number(row.desired_emission_factor_id),
          additional_information: String(row.additional_information || '').trim() || null,
        };

        await axios.post(`${API_URL}/custom-mappings`, data);
        results.success.push(i + 1);

        setRows(prev => {
          const newRows = [...prev];
          const rowIndex = prev.findIndex(r => r._id === row._id);
          if (rowIndex !== -1) {
            newRows[rowIndex] = { ...newRows[rowIndex], _status: 'submitted' };
          }
          return newRows;
        });
      } catch (error) {
        results.failed.push({
          row: i + 1,
          error: error.response?.data?.detail || error.message,
        });

        setRows(prev => {
          const newRows = [...prev];
          const rowIndex = prev.findIndex(r => r._id === row._id);
          if (rowIndex !== -1) {
            newRows[rowIndex] = {
              ...newRows[rowIndex],
              _status: 'failed',
              _errors: { _api: error.response?.data?.detail || error.message },
            };
          }
          return newRows;
        });
      }
    }

    setSubmitting(false);
    setSubmitResults(results);

    if (results.failed.length === 0) {
      toast.success(`Successfully submitted ${results.success.length} custom mappings`);
    } else if (results.success.length > 0) {
      toast.warning(`${results.success.length} submitted, ${results.failed.length} failed`);
    } else {
      toast.error('All submissions failed');
    }
  }, [rows]);

  const renderCell = (row, rowIndex, col, colIndex) => {
    const hasError = row._errors?.[col.key];
    const isSubmitted = row._status === 'submitted';

    const baseClass = `w-full h-8 px-2 text-sm border-0 focus:ring-1 focus:ring-primary outline-none ${
      hasError ? 'bg-red-50' : isSubmitted ? 'bg-green-50' : 'bg-white'
    }`;

    if (col.type === 'select') {
      const options = EMISSION_CATEGORIES.map(c => ({ value: c.label, label: c.label }));
      return (
        <Select
          value={row[col.key] || undefined}
          onValueChange={(value) => updateCell(rowIndex, col.key, value)}
          disabled={isSubmitted}
        >
          <SelectTrigger className={`${baseClass} border`} data-cell={`${rowIndex}-${colIndex}`}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="__empty__">—</SelectItem>
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <input
        onWheel={(e) => e.currentTarget.blur()} // number cells must not change on scroll
        type={col.type === 'number' ? 'number' : 'text'}
        value={row[col.key] ?? ''}
        onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
        className={baseClass}
        data-cell={`${rowIndex}-${colIndex}`}
        step={col.type === 'number' ? '1' : undefined}
        disabled={isSubmitted}
      />
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 animate-fade-in" data-testid="bulk-cm-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/custom-mappings')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground">Bulk Add Custom Mappings</h1>
              <p className="text-sm text-muted-foreground">Add multiple custom mappings at once - Excel-like interface</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadTemplate} data-testid="btn-download-template">
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xlsm"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="input-upload-excel"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile}
              data-testid="btn-upload-excel"
            >
              {uploadingFile ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Excel
            </Button>
            <div className="w-px bg-gray-300 mx-1" />
            <Button variant="outline" onClick={clearAll}>
              Clear All
            </Button>
            <Button variant="outline" onClick={() => addRows(5)}>
              <Plus className="w-4 h-4 mr-2" />
              Add 5 Rows
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="btn-primary">
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Submit All
            </Button>
          </div>
        </div>

        {/* Instructions */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <div className="flex items-start gap-2 text-sm text-blue-800">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Tips:</strong>
                <span className="ml-1">
                  <strong>Upload Excel</strong> (.xlsx) for bulk imports — download the template first for the correct format.
                  You can also copy-paste from Excel (Ctrl+V) or fill the grid manually. Required fields are marked with *.
                  The Emission Factor ID must reference an existing approved emission factor.
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Results */}
        {uploadResults && (
          <Card
            className={
              uploadResults.failed.length > 0 && uploadResults.success.length === 0
                ? 'border-red-200 bg-red-50'
                : uploadResults.failed.length > 0
                ? 'border-amber-200 bg-amber-50'
                : 'border-green-200 bg-green-50'
            }
            data-testid="upload-results"
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-2 text-sm">
                <FileSpreadsheet className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  uploadResults.failed.length > 0 && uploadResults.success.length === 0
                    ? 'text-red-600'
                    : uploadResults.failed.length > 0
                    ? 'text-amber-600'
                    : 'text-green-600'
                }`} />
                <div className="flex-1">
                  <div className="font-semibold">
                    Excel Upload Results — Total rows: {uploadResults.total}
                  </div>
                  <div className="mt-1 space-x-4">
                    <span className="text-green-700">
                      ✓ {uploadResults.success.length} created
                    </span>
                    {uploadResults.failed.length > 0 && (
                      <span className="text-red-600">
                        ✗ {uploadResults.failed.length} failed
                      </span>
                    )}
                  </div>
                  {uploadResults.failed.length > 0 && (
                    <div className="mt-3 max-h-48 overflow-y-auto bg-white rounded border border-red-200 p-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-red-100 text-red-800">
                            <th className="p-1 text-left w-20">Row</th>
                            <th className="p-1 text-left">Errors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadResults.failed.map((f, idx) => (
                            <tr key={idx} className="border-t border-red-100">
                              <td className="p-1 font-mono">{f.row}</td>
                              <td className="p-1 text-red-700">
                                {(f.errors || []).join('; ')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {uploadResults.success.length > 0 && (
                    <div className="mt-3">
                      <Button size="sm" variant="outline" onClick={() => navigate('/custom-mappings')}>
                        View Custom Mappings →
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Results */}
        {submitResults && (
          <Card className={submitResults.failed.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}>
            <CardContent className="p-3">
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${submitResults.failed.length > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                <div>
                  <strong>{submitResults.success.length} submitted successfully</strong>
                  {submitResults.failed.length > 0 && (
                    <span className="text-red-600 ml-2">
                      • {submitResults.failed.length} failed
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Spreadsheet */}
        <Card className="border-gray-200">
          <CardContent className="p-0">
            <div
              className="overflow-auto max-h-[calc(100vh-300px)]"
              ref={tableRef}
              onPaste={handlePaste}
            >
              <table className="w-full border-collapse" style={{ minWidth: COLUMNS.reduce((sum, col) => sum + col.width, 100) }}>
                <thead className="sticky top-0 z-10 bg-gray-100">
                  <tr>
                    <th className="w-12 p-2 text-xs font-medium text-gray-600 border-b border-r bg-gray-100 sticky left-0">#</th>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="p-2 text-xs font-medium text-gray-600 border-b border-r text-left whitespace-nowrap"
                        style={{ minWidth: col.width, width: col.width }}
                      >
                        {col.label}
                        {col.required && <span className="text-red-500 ml-0.5">*</span>}
                      </th>
                    ))}
                    <th className="w-10 p-2 border-b bg-gray-100"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr
                      key={row._id}
                      className={`
                        ${row._status === 'error' ? 'bg-red-50' : ''}
                        ${row._status === 'submitted' ? 'bg-green-50' : ''}
                        ${row._status === 'failed' ? 'bg-red-100' : ''}
                        hover:bg-gray-50
                      `}
                    >
                      <td className="p-1 text-xs text-center text-gray-500 border-b border-r bg-gray-50 sticky left-0">
                        <div className="flex items-center justify-center gap-1">
                          <span>{rowIndex + 1}</span>
                          {row._status === 'submitted' && <CheckCircle2 className="w-3 h-3 text-green-600" />}
                          {row._status === 'error' && <AlertCircle className="w-3 h-3 text-red-500" />}
                          {row._status === 'failed' && <AlertCircle className="w-3 h-3 text-red-600" />}
                        </div>
                      </td>
                      {COLUMNS.map((col, colIndex) => (
                        <td
                          key={col.key}
                          className="p-0 border-b border-r relative"
                          style={{ minWidth: col.width }}
                        >
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="relative">
                                  {renderCell(row, rowIndex, col, colIndex)}
                                </div>
                              </TooltipTrigger>
                              {row._errors?.[col.key] && (
                                <TooltipContent side="top" className="bg-red-600 text-white">
                                  {row._errors[col.key]}
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      ))}
                      <td className="p-1 border-b">
                        {row._status !== 'submitted' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => deleteRow(rowIndex)}
                          >
                            <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {rows.length} rows • {rows.filter(rowHasData).length} with data
              </div>
              <Button variant="outline" size="sm" onClick={() => addRows(1)}>
                <Plus className="w-3 h-3 mr-1" />
                Add Row
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Confirm Dialog */}
        <AlertDialog open={confirmDialog} onOpenChange={setConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Submit Custom Mappings?</AlertDialogTitle>
              <AlertDialogDescription>
                You are about to submit {rows.filter(r => rowHasData(r) && Object.keys(r._errors || {}).length === 0).length} custom mappings.
                They will be sent for approval.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmSubmit} className="btn-primary">
                Submit All
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
